/**
 * The reason a mutation carries, and how it is serialised into an API that
 * wants one string.
 *
 * # The mismatch this module exists for
 *
 * Law 21 says every mutation states what will be audited before it runs —
 * actor, action, subject, **reason code** — and that reason codes come from the
 * managed registry: a select, never free text. The design brief says the same
 * thing at §5.4.
 *
 * `POST /v1/admin/handles/reviews/{id}/decision` takes **one free-text `reason`
 * string** and has no reason-code field. So does §6.13's support-routing write.
 * §6.8.1's tax-rule publish is the counter-example that has a validated
 * `reason_code`, which is what makes this a mismatch between the console's law
 * and two particular endpoints rather than a rule nobody meant.
 *
 * The brief is explicit that the convention must be designed once rather than
 * invented per console, and that the two endpoints must adopt ONE convention.
 * This module is that one convention, and it lives here — not inline in
 * `handles.ts` — so the support-routing write joins it rather than inventing a
 * second.
 *
 * # The convention: code, then note
 *
 *     impersonation_likely: reads as the operator's own support account
 *
 * Code first because it is what an operator greps for six months later, and it
 * has to survive being truncated by whatever reads the audit row. The separator
 * is `': '` — a colon and one space — because the codes below are
 * `[a-z0-9_]` and can therefore never contain one, which makes the split
 * unambiguous in the other direction.
 *
 * # The 200-unit cap is the server's, and it refuses rather than truncates
 *
 * `maxReasonLen` in `internal/server/admin.go` is 200, checked after trimming,
 * and an over-long reason is a `400` — not a silently shortened audit row.
 * So the composer has to count against the same number the server does, before
 * the click, or the operator loses their reasoning to a rejected request.
 *
 * # And it counts BYTES, because the server counts bytes
 *
 * The server's check is Go's `len()` on a `string`, and a Go string is a byte
 * slice: `len()` is the UTF-8 byte length, never a count of characters. A
 * console measuring `String.length` measures UTF-16 code units instead, and the
 * two agree only on ASCII. They disagree on exactly the reasons this endpoint
 * exists for — an impersonation review is about names, and the note explaining
 * one quotes them: Cyrillic and Greek homoglyphs are two bytes each, a
 * fullwidth Latin lookalike is three, an emoji four.
 *
 * Measured in code units, a 160-character Cyrillic note reads as "40 left" with
 * a live submit, and the operator loses the whole reasoning to a 400 on the
 * click. Measured in bytes it reads as over the cap while they are still
 * typing, which is the only place the correction is free. So every measurement
 * in this module goes through `reasonBytes`, and `REASON_MAX_LENGTH` is a count
 * of bytes.
 */

/**
 * The server's own cap on a reason string (`maxReasonLen`, `admin.go`).
 *
 * **Bytes, not characters** — see above. The name is kept because it is the
 * server's constant's meaning, and every use of it here goes through
 * `reasonBytes`.
 */
export const REASON_MAX_LENGTH = 200

/** What separates the code from the note. Never occurs inside a code. */
const SEPARATOR = ': '

/**
 * One code an operator may choose, and what it means in words.
 *
 * `gloss` is the console's own plain-language reading and is shown beside the
 * code, never instead of it — the same discipline §6.18.3 requires of a claim's
 * `refusal_code`.
 */
export interface ReasonCode {
  readonly code: string
  readonly gloss: string
}

/**
 * Code and note, joined for the wire.
 *
 * Trims the note, because the server trims before measuring and a console that
 * counted untrimmed characters would refuse a reason the server would have
 * taken. An empty note serialises to the bare code: the code alone is a
 * complete, reviewable reason, and padding it with a separator and nothing
 * after would put a dangling colon in the audit row for ever.
 */
export function serialiseReason(code: string, note: string): string {
  const tail = note.trim()
  return tail === '' ? code : `${code}${SEPARATOR}${tail}`
}

/**
 * One encoder, reused. Constructing one per keystroke would be wasteful and,
 * more to the point, there is only one encoding to be had: `TextEncoder` is
 * UTF-8 by specification and takes no argument, which is the same encoding a Go
 * string holds.
 */
const UTF8 = new TextEncoder()

/**
 * How many BYTES the serialised reason takes on the wire.
 *
 * This is the measurement the server makes, and the only one this module is
 * allowed to make. `String.length` counts UTF-16 code units and would let a
 * non-ASCII reason past a check the server then fails.
 */
export function reasonBytes(code: string, note: string): number {
  return UTF8.encode(serialiseReason(code, note)).length
}

/**
 * Why this reason cannot be sent, or `null` when it can.
 *
 * Every branch is a sentence an operator reads at the field, before the click.
 * The alternative is the server's 400, which arrives after they have composed
 * the whole thing and says the same in fewer words.
 *
 * The over-length sentence says "bytes" rather than "characters" because on a
 * reason that trips this branch the two numbers differ, and a message quoting a
 * count the operator cannot see in their own text is worse than no count: they
 * would delete four characters, watch nothing change, and conclude the console
 * is broken.
 */
export function reasonRefusal(code: string, note: string): string | null {
  if (code === '') return 'Choose a reason code. The audit row is what this decision is answerable to.'
  const bytes = reasonBytes(code, note)
  if (bytes > REASON_MAX_LENGTH) {
    return `The reason is ${String(bytes)} bytes and the backend accepts ${String(REASON_MAX_LENGTH)}. It is refused rather than truncated, so shorten the note here — nothing is lost silently. Accented and non-Latin letters cost more than one byte each, which is why this can exceed the cap before it looks long.`
  }
  return null
}

/**
 * How many bytes remain, for the counter beside the field.
 *
 * Measured on the SERIALISED string rather than on the note, because the code
 * and its separator are part of what the server counts and a counter that
 * ignored them would read "40 left" on a reason the server refuses. Measured in
 * bytes for the same reason in the other dimension.
 */
export function reasonRemaining(code: string, note: string): number {
  return REASON_MAX_LENGTH - reasonBytes(code, note)
}
